import { components as c } from "@/components";
import {
    BuilderFormSelectDialog,
    BuilderScheduleSelectDialog,
} from "@/features/builder/components/BuilderSelectDialogs";
import { filterBuilderPickerItems } from "@/features/builder/utils/builderAccess";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useSelectFormsLite } from "@/features/forms/api/selectForms";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TrashIcon } from "lucide-react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { Button } from "@patient-watch/ui/button";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import { getSharedAttributeContactId } from "@/features/shared_attributes/components/SharedAttributeDisplay";

const trackerItemSchema = z.object({
    form_id: z.string().uuid(),
    schedule_id: z.string().uuid(),
});

type QuestionnairesFormValues = {
    trackers: { form_id: string; schedule_id: string }[];
};

const AddQuestionnairePage = () => {
    const copy = usePatientsCopy();
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type, reference_date, shared_attribute_id } = search;
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const { data: forms = [] } = useSelectFormsLite();
    const { data: schedules = [] } = useSchedulesQuery();
    const auth = useAuth();
    const navigate = useNavigate();

    const initialTrackers = (() => {
        const t = search.trackers;
        if (!Array.isArray(t) || t.length === 0) return [{ form_id: "", schedule_id: "" }];
        const mapped = t
            .filter((x): x is { form_id: string; schedule_id: string } => Boolean(x?.form_id && x?.schedule_id))
            .map((x) => ({ form_id: x.form_id, schedule_id: x.schedule_id }));
        return mapped.length > 0 ? mapped : [{ form_id: "", schedule_id: "" }];
    })();

    const form = useForm<QuestionnairesFormValues>({
        resolver: zodResolver(z.object({ trackers: z.array(trackerItemSchema) })),
        defaultValues: {
            trackers: initialTrackers,
        },
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: "trackers",
    });

    const watchedTrackers = useWatch({ control: form.control, name: "trackers" });

    const allValid = watchedTrackers?.length > 0 && watchedTrackers.every((t) => t.form_id && t.schedule_id);

    const goAfterQuestionnaires = (trackers?: { form_id: string; schedule_id: string }[]) => {
        const nextSearch = {
            ...search,
            reference_date,
            type,
            ...(trackers ? { trackers } : {}),
        };
        const contactId = diary ? getSharedAttributeContactId(diary, shared_attribute_id) : null;
        if (shared_attribute_id && contactId) {
            void navigate({
                to: "/patients/$diaryId/update/$eventId/$trackerId/message",
                params: { diaryId, eventId, trackerId },
                search: { ...nextSearch, contact_id: contactId },
            });
            return;
        }
        void navigate({
            to: "/patients/$diaryId/update/$eventId/$trackerId/contact",
            params: { diaryId, eventId, trackerId },
            search: nextSearch,
        });
    };

    const handleSubmit = (data: QuestionnairesFormValues) => {
        goAfterQuestionnaires(data.trackers);
    };

    const handleSkipQuestionnaires = () => {
        goAfterQuestionnaires();
    };

    const publishedForms = filterBuilderPickerItems(
        (forms ?? []).filter((f) => !!f.form_id),
        { isAdmin: auth.roles.isAdmin, userId: auth.id }
    );
    const publishedSchedules = filterBuilderPickerItems(
        (schedules ?? []).filter((s) => !!s.schedule_id),
        { isAdmin: auth.roles.isAdmin, userId: auth.id }
    );

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <c.cc.Title words={copy.updateQuestionnairesTitle} description={copy.updateQuestionnairesDescription} />

                <div className="space-y-4">
                    {fields.map((field, index) => (
                        <c.ui.Card key={field.id}>
                            <c.ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                                <c.ui.CardTitle className="text-base">
                                    {fields.length > 1 ? copy.questionnaireWithIndex(index + 1) : copy.questionnaire}
                                </c.ui.CardTitle>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => remove(index)}
                                    disabled={fields.length === 1}
                                >
                                    <TrashIcon size={18} />
                                </Button>
                            </c.ui.CardHeader>
                            <c.ui.CardContent className="flex md:flex-row flex-col items-end gap-4">
                                <div className="flex-1 w-full min-w-0">
                                    <BuilderFormSelectDialog
                                        name={`trackers.${index}.form_id`}
                                        control={form.control}
                                        options={publishedForms as any}
                                        label={index === 0 ? copy.selectQuestionnaire : undefined}
                                        description={index === 0 ? copy.selectQuestionnaireHelp : undefined}
                                    />
                                </div>
                                <div className="flex-1 w-full min-w-0">
                                    <BuilderScheduleSelectDialog
                                        name={`trackers.${index}.schedule_id`}
                                        control={form.control}
                                        options={publishedSchedules as any}
                                        label={index === 0 ? copy.selectSchedule : undefined}
                                        description={index === 0 ? copy.selectScheduleHelp : undefined}
                                    />
                                </div>
                            </c.ui.CardContent>
                        </c.ui.Card>
                    ))}
                </div>

                <fc.AddButton
                    buttonType="button"
                    variant="secondary"
                    label={copy.addAnotherQuestionnaire}
                    onClick={() => append({ form_id: "", schedule_id: "" })}
                    className="w-full"
                />

                <fc.ContinueButton buttonType="submit" size="lg" disabled={!allValid} className="w-full" />

                <fc.ContinueButton
                    buttonType="button"
                    variant="outline"
                    size="lg"
                    label={copy.continueWithoutQuestionnaires}
                    onClick={handleSkipQuestionnaires}
                    className="w-full"
                />
            </form>
        </c.ui.Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/questionnaire")({
    component: AddQuestionnairePage,
    validateSearch: updateDiaryFlowSearchSchema,
});
